import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Download as DownloadLucide, Plus, Search, X, Trash2,
  ArrowRight as CarryRight, Pencil, CheckCircle2, Send, Mail,
  Repeat, Ban, Sparkles, Clock, Paperclip, Loader2, Download,
} from 'lucide-react';
import {
  Tooltip, TooltipContent, TooltipTrigger,
} from '@/components/ui/tooltip';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { useLocation } from 'react-router-dom';
import { format } from 'date-fns';
import { workItemsApi, categoriesApi, tagsApi, authApi, projectsApi, driversApi, exportApi, rhythmApi } from '../api/services';
import type { WorkItem, WorkItemAttachment, WorkItemType, Priority, WorkItemStatus, WorkItemFlag, Category, Tag, Project, AnnualStrategicDriver } from '../types/models';
import PriorityChip from '../components/PriorityChip';
import StatusChip from '../components/StatusChip';
import HelpPanel from '../components/HelpPanel';
import DriverPill from '../components/DriverPill';
import { WorkItemTypeBadge } from '../components/workItems/WorkItemTypeTreatment';
import { isAllowedEmailLink, openEmailLink } from '../utils/emailLink';
import { suggestWorkItemType } from '../utils/suggestWorkItemType';
import { workItemsCopy } from '../copy/workItems';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { TagInput, ChipPicker } from '@/components/ui/combobox';
import { useConfirm } from '@/components/ui/confirm-dialog';
import { PageHeader } from '@/components/ui/page-header';
import { LinearProgressBar } from '@/components/ui/linear-progress';
import { useToast } from '@/components/ui/toast';
import { MicrosoftIcon, GoogleIcon, brandForUrl, brandLabel } from '../components/icons/BrandIcons';

const todayStr = format(new Date(), 'yyyy-MM-dd');

function getCarryForwardDays(item: WorkItem): number | null {
  if (!item.isCarryForward || !item.originalDate) return null;
  return Math.ceil((new Date().getTime() - new Date(item.originalDate).getTime()) / 86400000);
}

// Description bodies that come from transcript-derived work items often end
// with a `Source: https://...` line. Render the URL as a clickable brand-icon
// button instead of dumping the raw query-string into prose.
function splitDescriptionSource(text: string): { body: string; sourceUrl: string | null } {
  if (!text) return { body: text, sourceUrl: null };
  // Match a "Source:" label (case-insensitive) followed by an http(s) URL,
  // anchored to a line. Allow trailing whitespace / closing punctuation.
  const match = text.match(/^\s*source\s*:\s*(https?:\/\/\S+)\s*$/im);
  if (!match || match.index === undefined) return { body: text, sourceUrl: null };
  const before = text.slice(0, match.index).replace(/\s+$/, '');
  const after = text.slice(match.index + match[0].length).replace(/^\s+/, '');
  const body = [before, after].filter(Boolean).join('\n\n');
  return { body, sourceUrl: match[1] };
}

const FLAG_OPTIONS: WorkItemFlag[] = [
  'Leadership', 'Dependency', 'Urgent', 'Informational',
  'FollowUp', 'DecisionRequired',
];

const workItemTypes: WorkItemType[] = ['Task', 'Decision', 'Briefing', 'Review', 'FollowUp', 'Draft', 'Reading'];
const priorities: Priority[] = ['Critical', 'High', 'Medium', 'Low'];
const statuses: WorkItemStatus[] = ['Backlog', 'Planned', 'InProgress', 'Done', 'Deferred', 'Cancelled'];
const recurrencePatterns = ['Daily', 'Weekly', 'Biweekly', 'Monthly', 'Quarterly'];
const weekDays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

export default function WorkItemsPage() {
  const location = useLocation();
  const confirm = useConfirm();
  const { toast } = useToast();
  const [items, setItems] = useState<WorkItem[]>([]);
  const [loading, setLoading] = useState(true); // eslint-disable-line @typescript-eslint/no-unused-vars
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState<Partial<WorkItem> | null>(null);
  const [viewItem, setViewItem] = useState<WorkItem | null>(null);
  const [tabValue, setTabValue] = useState(0);

  // Bulk selection
  const [selected, setSelected] = useState<Set<string>>(new Set());

  // Search & filter
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState<string>('');
  const [filterPriority, setFilterPriority] = useState<string>('');

  // Categories & tags for the create/edit dialog
  const [categories, setCategories] = useState<Category[]>([]);
  const [allTags, setAllTags] = useState<Tag[]>([]);
  const [editTagNames, setEditTagNames] = useState<string[]>([]);
  const [editFlags, setEditFlags] = useState<string[]>([]);

  // Delegation state
  const [delegateDialogOpen, setDelegateDialogOpen] = useState(false);
  const [delegateItem, setDelegateItem] = useState<WorkItem | null>(null);
  const [delegateTarget, setDelegateTarget] = useState('');
  const [delegateDate, setDelegateDate] = useState('');
  const [delegateNotes, setDelegateNotes] = useState('');
  const [executives, setExecutives] = useState<{ id: string; displayName: string; email: string }[]>([]);
  const [activeProjects, setActiveProjects] = useState<Project[]>([]);
  const [strategicDrivers, setStrategicDrivers] = useState<AnnualStrategicDriver[]>([]);

  // Attachments state
  const [attachments, setAttachments] = useState<WorkItemAttachment[]>([]);
  const [attachmentUploading, setAttachmentUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Slot suggestions
  const [slotSuggestions, setSlotSuggestions] = useState<Array<{ startTime: string; endTime: string; score: number; reason: string }>>([]);
  const [suggestingSlots, setSuggestingSlots] = useState(false);
  const [schedulingItem, setSchedulingItem] = useState(false);
  const [scheduleSuccess, setScheduleSuccess] = useState<string | null>(null);

  const loadItems = async () => {
    setLoading(true);
    try {
      const data = await workItemsApi.getAll();
      setItems(data);
    } catch (err) {
      // silent-by-design: empty list signals load failure visually;
      // every mutation handler refetches and surfaces its own toast.
      console.error('Failed to load work items', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadItems(); }, []);

  // Load categories, tags, executives, projects, and drivers once
  useEffect(() => {
    categoriesApi.getAll().then(setCategories).catch(() => {});
    tagsApi.getAll().then(setAllTags).catch(() => {});
    authApi.getUsers().then(users => {
      setExecutives(users.filter((u: any) => u.role === 'Executive'));
    }).catch(() => {});
    projectsApi.getAll({ status: 'Active' }).then(setActiveProjects).catch(() => {});
    driversApi.getAll().then(setStrategicDrivers).catch(() => {});
  }, []);

  // Open create dialog when navigated with ?action=create
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('action') === 'create' && !dialogOpen) {
      setEditItem({
        title: '', type: 'Task', priority: 'Medium', status: 'Backlog',
        estimatedMinutes: 30, targetDate: format(new Date(), 'yyyy-MM-dd'),
        flags: [],
      });
      setEditTagNames([]);
      setEditFlags([]);
      setAttachments([]);
      setSlotSuggestions([]);
      setDialogOpen(true);
    }
  }, [location.search]); // eslint-disable-line react-hooks/exhaustive-deps

  // Attachment helpers
  const loadAttachments = useCallback(async (workItemId: string) => {
    try {
      const data = await workItemsApi.getAttachments(workItemId);
      setAttachments(data);
    } catch {
      setAttachments([]);
    }
  }, []);

  const handleUploadAttachment = async (file: File) => {
    if (!editItem?.id) return;
    if (file.size > 5 * 1024 * 1024) {
      toast({ variant: 'error', message: 'File size exceeds 5 MB limit.' });
      return;
    }
    if (attachments.length >= 10) {
      toast({ variant: 'error', message: 'Maximum 10 attachments per work item.' });
      return;
    }
    setAttachmentUploading(true);
    try {
      await workItemsApi.uploadAttachment(editItem.id, file);
      await loadAttachments(editItem.id);
    } catch (err) {
      console.error('Failed to upload attachment', err);
      toast({ variant: 'error', message: 'Failed to upload attachment.' });
    } finally {
      setAttachmentUploading(false);
    }
  };

  const handleDeleteAttachment = async (attachmentId: string) => {
    if (!editItem?.id) return;
    const ok = await confirm({
      title: 'Delete attachment?',
      body: 'The file will be permanently removed from this work item and cannot be recovered.',
      confirmLabel: 'Delete',
      destructive: true,
    });
    if (!ok) return;
    try {
      await workItemsApi.deleteAttachment(editItem.id, attachmentId);
      await loadAttachments(editItem.id);
    } catch (err: any) {
      console.error('Failed to delete attachment', err);
      toast({ variant: 'error', message: err?.response?.data?.error ?? 'Failed to delete attachment' });
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Slot suggestion helpers
  const handleSuggestSlots = async () => {
    if (!editItem?.estimatedMinutes || !editItem?.targetDate) return;
    setSuggestingSlots(true);
    setSlotSuggestions([]);
    try {
      const result = await rhythmApi.suggestSlots(
        editItem.estimatedMinutes,
        editItem.targetDate,
        editItem.priority?.toLowerCase()
      );
      setSlotSuggestions(result.suggestions || []);
    } catch (err: any) {
      console.error('Failed to suggest slots', err);
      toast({ variant: 'error', message: err?.response?.data?.error ?? 'Failed to suggest slots' });
    } finally {
      setSuggestingSlots(false);
    }
  };

  const handleScheduleItem = async (workItemId: string, targetDate?: string) => {
    setSchedulingItem(true);
    setScheduleSuccess(null);
    try {
      const date = targetDate || editItem?.targetDate || todayStr;
      const result = await rhythmApi.autoSchedule({ date, workItemIds: [workItemId] });
      if (result.createdBlocks.length > 0) {
        const block = result.createdBlocks[0];
        const start = new Date(block.startTime);
        const end = new Date(block.endTime);
        const timeStr = `${start.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} - ${end.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
        setScheduleSuccess(`Scheduled for ${timeStr}`);
      } else if (result.conflicts.length > 0) {
        setScheduleSuccess(result.conflicts[0]);
      } else {
        setScheduleSuccess(result.message);
      }
      setSlotSuggestions([]);
      await loadItems();
    } catch (err: any) {
      console.error('Failed to schedule work item', err);
      toast({ variant: 'error', message: err?.response?.data?.error ?? 'Failed to schedule work item' });
    } finally {
      setSchedulingItem(false);
    }
  };

  // Dependency helpers
  const getBlockingItems = useCallback((itemId: string): WorkItem[] => {
    return items.filter(i => i.blockedByWorkItemId === itemId);
  }, [items]);

  const handleSave = async () => {
    if (!editItem?.title) return;
    // Block save on an invalid email link so the user sees the inline error
    // before the backend 400. An empty/whitespace link is allowed (clears).
    const rawLink = (editItem.emailLink ?? '').trim();
    if (rawLink.length > 0 && !isAllowedEmailLink(rawLink)) {
      return;
    }
    try {
      const payload = {
        ...editItem,
        emailLink: rawLink.length === 0 ? '' : rawLink,
        flags: editFlags as WorkItemFlag[],
        // Clear recurrence fields if not recurring
        ...(!editItem.isRecurring && {
          recurrencePattern: undefined,
          recurrenceDays: undefined,
          recurrenceEndDate: undefined,
        }),
      };
      if (editItem.id) {
        await workItemsApi.update(editItem.id, payload);
      } else {
        await workItemsApi.create(payload);
      }
      setDialogOpen(false);
      setEditItem(null);
      setEditFlags([]);
      loadItems();
    } catch (err: any) {
      console.error('Failed to save work item', err);
      toast({ variant: 'error', message: err?.response?.data?.error ?? 'Failed to save work item' });
    }
  };

  const handleOpenDelegate = (item: WorkItem) => {
    setDelegateItem(item);
    setDelegateTarget('');
    setDelegateDate(item.targetDate || '');
    setDelegateNotes('');
    setDelegateDialogOpen(true);
  };

  const handleDelegate = async () => {
    if (!delegateItem || !delegateTarget) return;
    try {
      await workItemsApi.delegate(delegateItem.id, {
        targetExecutiveUserId: delegateTarget,
        targetDate: delegateDate || undefined,
        notes: delegateNotes || undefined,
      });
      setDelegateDialogOpen(false);
      setDelegateItem(null);
      loadItems();
    } catch (err: any) {
      console.error('Failed to delegate work item', err);
      toast({ variant: 'error', message: err?.response?.data?.error ?? 'Failed to delegate work item' });
    }
  };

  const handleComplete = async (id: string) => {
    try {
      await workItemsApi.update(id, { status: 'Done' });
      loadItems();
    } catch (err: any) {
      console.error('Failed to complete', err);
      toast({ variant: 'error', message: err?.response?.data?.error ?? 'Failed to mark work item complete' });
    }
  };

  const handleDelete = async (id: string) => {
    const ok = await confirm({
      title: 'Delete work item?',
      body: 'This work item will be permanently removed. Linked transcripts and attachments stay, but the item itself cannot be recovered.',
      confirmLabel: 'Delete',
      destructive: true,
    });
    if (!ok) return;
    try {
      await workItemsApi.delete(id);
      loadItems();
    } catch (err: any) {
      console.error('Failed to delete', err);
      toast({ variant: 'error', message: err?.response?.data?.error ?? 'Failed to delete work item' });
    }
  };

  const handleCarryForward = async (id: string) => {
    const tomorrow = format(new Date(Date.now() + 86400000), 'yyyy-MM-dd');
    try {
      await workItemsApi.carryForward(id, tomorrow);
      loadItems();
    } catch (err: any) {
      console.error('Failed to carry forward', err);
      toast({ variant: 'error', message: err?.response?.data?.error ?? 'Failed to carry work item forward' });
    }
  };

  // Bulk actions
  const handleBulkCarryForward = async () => {
    const tomorrow = format(new Date(Date.now() + 86400000), 'yyyy-MM-dd');
    try {
      await Promise.all(Array.from(selected).map(id => workItemsApi.carryForward(id, tomorrow)));
      setSelected(new Set());
      loadItems();
    } catch (err: any) {
      console.error('Bulk carry forward failed', err);
      toast({ variant: 'error', message: err?.response?.data?.error ?? 'Failed to carry items forward' });
    }
  };

  const handleBulkComplete = async () => {
    // Skip items that are already Done — `update` would still succeed but it's
    // a wasted round-trip and pollutes the success/failure tally.
    const ids = Array.from(selected).filter(id => {
      const item = items.find(i => i.id === id);
      return item && item.status !== 'Done';
    });
    if (ids.length === 0) {
      setSelected(new Set());
      return;
    }
    try {
      await Promise.all(ids.map(id => workItemsApi.update(id, { status: 'Done' })));
      setSelected(new Set());
      loadItems();
      toast({
        variant: 'success',
        message: `Marked ${ids.length} item${ids.length === 1 ? '' : 's'} complete.`,
      });
    } catch (err: any) {
      console.error('Bulk complete failed', err);
      toast({ variant: 'error', message: err?.response?.data?.error ?? 'Failed to mark items complete' });
    }
  };

  const handleBulkDelete = async () => {
    const count = selected.size;
    const ok = await confirm({
      title: `Delete ${count} selected work ${count === 1 ? 'item' : 'items'}?`,
      body: 'All selected items will be permanently removed. This cannot be undone.',
      confirmLabel: `Delete ${count}`,
      destructive: true,
    });
    if (!ok) return;
    try {
      await Promise.all(Array.from(selected).map(id => workItemsApi.delete(id)));
      setSelected(new Set());
      loadItems();
    } catch (err: any) {
      console.error('Bulk delete failed', err);
      toast({ variant: 'error', message: err?.response?.data?.error ?? 'Failed to delete work items' });
    }
  };

  const toggleSelect = (id: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = (ids: string[]) => {
    const allSelected = ids.every(id => selected.has(id));
    if (allSelected) {
      setSelected(new Set());
    } else {
      setSelected(new Set(ids));
    }
  };

  // Tab filter — Today is now first (index 0)
  const todayItems = items.filter(i => i.targetDate === todayStr);
  const tabFiltered = tabValue === 0
    ? todayItems
    : tabValue === 1
      ? items.filter(i => !['Done', 'Cancelled'].includes(i.status))
      : tabValue === 2
        ? items.filter(i => i.status === 'Done')
        : items;

  // Search & filter
  const filteredItems = useMemo(() => {
    let result = tabFiltered;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(i =>
        i.title.toLowerCase().includes(q) || (i.description || '').toLowerCase().includes(q)
      );
    }
    if (filterType) result = result.filter(i => i.type === filterType);
    if (filterPriority) result = result.filter(i => i.priority === filterPriority);
    return result;
  }, [tabFiltered, searchQuery, filterType, filterPriority]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleExportCsv = async () => {
    try {
      const response = await exportApi.downloadWorkItemsCsv();
      const url = URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `work-items-${format(new Date(), 'yyyy-MM-dd')}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast({ variant: 'error', message: 'Failed to export work items.' });
    }
  };

  // Sentinel values for shadcn Select — Radix Select can't carry an empty-
  // string SelectItem value, so we round-trip "all"/"none" ↔ '' (or undefined)
  // at the boundary instead of changing the surrounding logic.
  const ALL = 'all';
  const NONE = '__none__';
  const tabLabels: Array<{ value: string; label: string }> = [
    { value: '0', label: `Today (${items.filter(i => i.targetDate === todayStr).length})` },
    { value: '1', label: `Active (${items.filter(i => !['Done', 'Cancelled'].includes(i.status)).length})` },
    { value: '2', label: `Completed (${items.filter(i => i.status === 'Done').length})` },
    { value: '3', label: `All (${items.length})` },
  ];

  return (
    <div>
      <PageHeader
        title="Work Items"
        className="mb-6"
        action={
          <>
            <Button variant="outline" onClick={handleExportCsv} className="gap-1.5">
              <DownloadLucide className="h-4 w-4" />
              Export CSV
            </Button>
            <Button
              className="gap-1.5"
              onClick={() => {
                setEditItem({
                  title: '', type: 'Task', priority: 'Medium', status: 'Backlog',
                  estimatedMinutes: 30, targetDate: format(new Date(), 'yyyy-MM-dd'),
                  flags: [],
                });
                setEditTagNames([]);
                setEditFlags([]);
                setAttachments([]);
                setSlotSuggestions([]);
                setDialogOpen(true);
              }}
            >
              <Plus className="h-4 w-4" />
              Add Work Item
            </Button>
          </>
        }
      />

      <HelpPanel title="About Work Items" storageKey="workitems">
        Work items are tasks, decisions, briefings, and reviews. Use flags to mark urgency. Drag to reorder priority.
      </HelpPanel>

      <Tabs
        value={String(tabValue)}
        onValueChange={(v) => { setTabValue(Number(v)); setSelected(new Set()); }}
        className="mb-4"
      >
        <TabsList>
          {tabLabels.map(t => (
            <TabsTrigger key={t.value} value={t.value}>{t.label}</TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {/* Search & filter bar */}
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="relative min-w-[240px]">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search title or description..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-8"
          />
        </div>
        <Select
          value={filterType || ALL}
          onValueChange={(v) => setFilterType(v === ALL ? '' : v)}
        >
          <SelectTrigger className="w-[140px]">
            <SelectValue placeholder="Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All types</SelectItem>
            {workItemTypes.map(t => (
              <SelectItem key={t} value={t}>{t}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={filterPriority || ALL}
          onValueChange={(v) => setFilterPriority(v === ALL ? '' : v)}
        >
          <SelectTrigger className="w-[140px]">
            <SelectValue placeholder="Priority" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All priorities</SelectItem>
            {priorities.map(p => (
              <SelectItem key={p} value={p}>{p}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {(searchQuery || filterType || filterPriority) && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => { setSearchQuery(''); setFilterType(''); setFilterPriority(''); }}
            className="gap-1.5"
          >
            <X className="h-3.5 w-3.5" />
            Clear
          </Button>
        )}
      </div>

      {/* Bulk actions toolbar — only when something is selected. */}
      {selected.size > 0 && (
        <div className="mb-2 flex h-12 items-center gap-3 rounded-2xl bg-primary/10 px-4">
          <p className="flex-1 text-sm font-semibold">{selected.size} selected</p>
          <Button size="sm" variant="outline" onClick={handleBulkComplete} className="gap-1.5">
            <CheckCircle2 className="h-3.5 w-3.5" />
            Mark Complete
          </Button>
          <Button size="sm" variant="outline" onClick={handleBulkCarryForward} className="gap-1.5">
            <CarryRight className="h-3.5 w-3.5" />
            Carry Forward All
          </Button>
          <Button size="sm" variant="destructive" onClick={handleBulkDelete} className="gap-1.5">
            <Trash2 className="h-3.5 w-3.5" />
            Delete Selected
          </Button>
        </div>
      )}

      <div className="overflow-x-auto rounded-2xl border border-border bg-card/40 backdrop-blur">
        <table className="w-full text-sm">
          <thead className="bg-muted/40">
            <tr className="border-b border-border text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
              <th className="w-10 px-2 py-2">
                <Checkbox
                  checked={
                    filteredItems.length > 0 && selected.size === filteredItems.length
                      ? true
                      : selected.size > 0 && selected.size < filteredItems.length
                        ? 'indeterminate'
                        : false
                  }
                  onCheckedChange={() => toggleSelectAll(filteredItems.map(i => i.id))}
                  aria-label="Select all"
                />
              </th>
              <th className="px-3 py-2">Title</th>
              <th className="px-3 py-2">Type</th>
              <th className="px-3 py-2">Priority</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">Duration</th>
              <th className="px-3 py-2">Date</th>
              <th className="px-3 py-2">Flags</th>
              <th className="px-3 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredItems.map((item) => {
              const isDone = item.status === 'Done';
              const isCancelled = item.status === 'Cancelled';
              const rowSelected = selected.has(item.id);
              const blockingCount = getBlockingItems(item.id).length;
              const blockedByTitle = item.blockedByWorkItemId
                ? items.find(i => i.id === item.blockedByWorkItemId)?.title || 'Unknown'
                : null;
              return (
                <tr
                  key={item.id}
                  onClick={() => setViewItem(item)}
                  // Keyboard parity for the row click — Enter and Space both
                  // open the detail dialog, matching the implicit semantics of
                  // a button. We intentionally don't use role="link" because
                  // the row doesn't navigate; the dialog is in-place.
                  tabIndex={0}
                  role="button"
                  aria-label={`View ${item.title}`}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      // Don't hijack key events that bubbled up from an
                      // interactive descendant (Checkbox, action button, etc.).
                      if (e.target !== e.currentTarget) return;
                      e.preventDefault();
                      setViewItem(item);
                    }
                  }}
                  className={cn(
                    'cursor-pointer border-b border-border last:border-b-0 transition-colors hover:bg-muted/30 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                    rowSelected && 'bg-primary/5',
                    (isDone || isCancelled) && 'opacity-50',
                  )}
                >
                  <td className="px-2 py-2" onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      checked={rowSelected}
                      onCheckedChange={() => toggleSelect(item.id)}
                      aria-label={`Select ${item.title}`}
                    />
                  </td>
                  <td className="min-w-0 px-3 py-2">
                    <div className="flex min-w-0 items-center gap-2">
                      <p className={cn(
                        'truncate text-sm font-medium',
                        (isDone || isCancelled) && 'line-through',
                      )}>
                        {isDone && '✓ '}{item.title}
                      </p>
                      <DriverPill driver={item.strategicDriver} size="compact" />
                    </div>
                    {item.description && (
                      <p className="mt-0.5 truncate text-xs text-muted-foreground">
                        {item.description.substring(0, 80)}{item.description.length > 80 ? '...' : ''}
                      </p>
                    )}
                  </td>
                  <td className="px-3 py-2"><WorkItemTypeBadge type={item.type} variant="full" /></td>
                  <td className="px-3 py-2"><PriorityChip priority={item.priority} /></td>
                  <td className="px-3 py-2"><StatusChip status={item.status} /></td>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-1.5 text-xs">
                      <span>{item.estimatedMinutes}m</span>
                      {item.aiEstimatedMinutes != null && item.aiEstimatedMinutes !== item.estimatedMinutes && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="inline-flex items-center gap-0.5 rounded-full border border-blue-500/60 px-1.5 py-0.5 text-[10px] font-medium text-blue-600 dark:text-blue-400">
                              <Sparkles className="h-3 w-3" />
                              {item.aiEstimatedMinutes}m
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>AI estimated: {item.aiEstimatedMinutes}m</TooltipContent>
                        </Tooltip>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-xs">{item.targetDate}</td>
                  <td className="px-3 py-2">
                    <div className="flex flex-wrap items-center gap-1">
                      {item.isCarryForward && (() => {
                        const days = getCarryForwardDays(item);
                        const overdue = days != null && days > 3;
                        return (
                          <span className={cn(
                            'inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium',
                            overdue
                              ? 'border-rose-500/60 text-rose-600 dark:text-rose-400'
                              : 'border-amber-500/60 text-amber-700 dark:text-amber-400',
                          )}>
                            {days != null ? `Carried ${days} day${days !== 1 ? 's' : ''}` : 'Carried'}
                          </span>
                        );
                      })()}
                      {item.isRecurring && !item.recurrenceParentId && (
                        <span className="inline-flex items-center gap-0.5 rounded-full border border-blue-500/60 px-2 py-0.5 text-[10px] font-medium text-blue-600 dark:text-blue-400">
                          <Repeat className="h-3 w-3" />
                          {item.recurrencePattern || 'Recurring'}
                        </span>
                      )}
                      {item.recurrenceParentId && (
                        <span className="inline-flex items-center gap-0.5 rounded-full border border-border px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
                          <Repeat className="h-3 w-3" />
                          Instance
                        </span>
                      )}
                      {item.blockedByWorkItemId && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="inline-flex items-center gap-0.5 rounded-full border border-rose-500/60 px-2 py-0.5 text-[10px] font-medium text-rose-600 dark:text-rose-400">
                              <Ban className="h-3 w-3" />
                              Blocked
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>Blocked by: {blockedByTitle}</TooltipContent>
                        </Tooltip>
                      )}
                      {blockingCount > 0 && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="inline-flex items-center gap-0.5 rounded-full border border-amber-500/60 px-2 py-0.5 text-[10px] font-medium text-amber-700 dark:text-amber-400">
                              <Ban className="h-3 w-3" />
                              Blocking {blockingCount}
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>
                            Blocking: {getBlockingItems(item.id).map(i => i.title).join(', ')}
                          </TooltipContent>
                        </Tooltip>
                      )}
                      {item.flags?.filter(f => f !== 'CarryForward').map(f => (
                        <span key={f} className="inline-flex items-center rounded-full border border-border px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
                          {f}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center justify-end gap-0.5">
                      {isAllowedEmailLink(item.emailLink) && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button size="icon-sm" variant="ghost" onClick={() => openEmailLink(item.emailLink)}>
                              <Mail className="h-4 w-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>{workItemsCopy.openEmail}</TooltipContent>
                        </Tooltip>
                      )}
                      {!isDone && !isCancelled && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              size="icon-sm"
                              variant="ghost"
                              onClick={() => handleComplete(item.id)}
                              className="text-emerald-600 hover:text-emerald-700 dark:text-emerald-400"
                            >
                              <CheckCircle2 className="h-4 w-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Mark complete</TooltipContent>
                        </Tooltip>
                      )}
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            size="icon-sm"
                            variant="ghost"
                            onClick={() => {
                              setEditItem(item);
                              setEditTagNames(item.workItemTags?.map(wt => wt.tag?.name || '').filter(Boolean) as string[] || []);
                              setEditFlags(item.flags?.filter(f => !['CarryForward', 'ImageDerived'].includes(f)) || []);
                              loadAttachments(item.id);
                              setDialogOpen(true);
                            }}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Edit</TooltipContent>
                      </Tooltip>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button size="icon-sm" variant="ghost" onClick={() => handleCarryForward(item.id)}>
                            <CarryRight className="h-4 w-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Carry forward to tomorrow</TooltipContent>
                      </Tooltip>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button size="icon-sm" variant="ghost" onClick={() => handleOpenDelegate(item)}>
                            <Send className="h-4 w-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Delegate</TooltipContent>
                      </Tooltip>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button size="icon-sm" variant="ghost" onClick={() => handleDelete(item.id)}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Delete</TooltipContent>
                      </Tooltip>
                    </div>
                  </td>
                </tr>
              );
            })}
            {filteredItems.length === 0 && (
              <tr>
                <td colSpan={9} className="py-10 text-center text-sm text-muted-foreground">
                  No work items
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(o) => !o && setDialogOpen(false)}>
        <DialogContent className="max-h-[85vh] max-w-lg overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editItem?.id ? 'Edit Work Item' : 'New Work Item'}</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="wi-title">Title</Label>
              <Input
                id="wi-title"
                value={editItem?.title || ''}
                onChange={(e) => setEditItem(prev => ({ ...prev, title: e.target.value }))}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="wi-desc">Description</Label>
              <Textarea
                id="wi-desc"
                rows={3}
                value={editItem?.description || ''}
                onChange={(e) => setEditItem(prev => ({ ...prev, description: e.target.value }))}
              />
            </div>

            {/* Type suggestion — only when current type is the default 'Task'
                AND the title/description matches a non-Task pattern. */}
            {(() => {
              const currentType = editItem?.type || 'Task';
              if (currentType !== 'Task') return null;
              const suggestion = suggestWorkItemType(editItem?.title || '', editItem?.description);
              if (!suggestion) return null;
              return (
                <div className="-mt-1 flex items-center gap-2">
                  <p className="text-xs text-muted-foreground">
                    Looks like a <strong className="text-foreground">{suggestion.type}</strong> — {suggestion.reason}.
                  </p>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-6 px-2 text-[11px]"
                    onClick={() => setEditItem(prev => ({ ...prev, type: suggestion.type as WorkItemType }))}
                  >
                    Use {suggestion.type}
                  </Button>
                </div>
              );
            })()}

            <div className="grid grid-cols-2 gap-3">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="wi-type">Type</Label>
                <Select
                  value={editItem?.type || 'Task'}
                  onValueChange={(v) => setEditItem(prev => ({ ...prev, type: v as WorkItemType }))}
                >
                  <SelectTrigger id="wi-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {workItemTypes.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="wi-priority">Priority</Label>
                <Select
                  value={editItem?.priority || 'Medium'}
                  onValueChange={(v) => setEditItem(prev => ({ ...prev, priority: v as Priority }))}
                >
                  <SelectTrigger id="wi-priority"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {priorities.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="wi-status">Status</Label>
                <Select
                  value={editItem?.status || 'Backlog'}
                  onValueChange={(v) => setEditItem(prev => ({ ...prev, status: v as WorkItemStatus }))}
                >
                  <SelectTrigger id="wi-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {statuses.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="wi-duration">Duration (min)</Label>
                <Input
                  id="wi-duration"
                  type="number"
                  value={editItem?.estimatedMinutes || 30}
                  onChange={(e) => setEditItem(prev => ({ ...prev, estimatedMinutes: parseInt(e.target.value) || 30 }))}
                />
              </div>
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="wi-target-date">Target Date</Label>
              <Input
                id="wi-target-date"
                type="date"
                value={editItem?.targetDate || ''}
                onChange={(e) => { setEditItem(prev => ({ ...prev, targetDate: e.target.value })); setSlotSuggestions([]); }}
              />
            </div>

            {editItem?.estimatedMinutes && editItem?.targetDate && (
              <div>
                <div className="mb-2 flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleSuggestSlots}
                    disabled={suggestingSlots || schedulingItem}
                    className="gap-1.5"
                  >
                    <Clock className="h-3.5 w-3.5" />
                    {suggestingSlots ? 'Finding slots...' : 'Suggest Time'}
                  </Button>
                  {editItem?.id && (
                    <Button
                      size="sm"
                      onClick={() => handleScheduleItem(editItem!.id!)}
                      disabled={schedulingItem || suggestingSlots}
                      className="gap-1.5"
                    >
                      {schedulingItem
                        ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        : <Clock className="h-3.5 w-3.5" />}
                      {schedulingItem ? 'Scheduling...' : 'Schedule'}
                    </Button>
                  )}
                </div>
                {scheduleSuccess && (
                  <Alert variant="success" className="mb-2 py-2">
                    <div className="flex items-start justify-between gap-2">
                      <AlertDescription className="flex-1">{scheduleSuccess}</AlertDescription>
                      <button
                        type="button"
                        onClick={() => setScheduleSuccess(null)}
                        aria-label="Dismiss"
                        className="text-muted-foreground hover:text-foreground"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </Alert>
                )}
                {suggestingSlots && <LinearProgressBar className="mb-2" />}
                {slotSuggestions.length > 0 && (
                  <div className="flex flex-col gap-1">
                    {slotSuggestions.map((slot, idx) => {
                      const start = new Date(slot.startTime);
                      const end = new Date(slot.endTime);
                      const timeStr = `${start.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} - ${end.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
                      const scorePercent = Math.round(slot.score * 100);
                      const scoreTone = scorePercent >= 80
                        ? 'border-emerald-500/60 text-emerald-700 dark:text-emerald-400'
                        : scorePercent >= 60
                          ? 'border-amber-500/60 text-amber-700 dark:text-amber-400'
                          : 'border-border text-muted-foreground';
                      return (
                        <button
                          key={idx}
                          type="button"
                          onClick={() => {
                            const dateStr = format(start, 'yyyy-MM-dd');
                            setEditItem(prev => ({ ...prev, targetDate: dateStr }));
                            setSlotSuggestions([]);
                          }}
                          className={cn(
                            'flex items-center justify-between rounded-lg border bg-card/40 p-2 text-left transition-colors hover:bg-muted/40',
                            idx === 0 ? 'border-primary' : 'border-border',
                          )}
                        >
                          <div>
                            <p className="text-sm font-semibold">{timeStr}</p>
                            <p className="text-xs text-muted-foreground">{slot.reason}</p>
                          </div>
                          <span className={cn(
                            'inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-semibold',
                            scoreTone,
                          )}>
                            {scorePercent}%
                          </span>
                        </button>
                      );
                    })}
                    <p className="mt-1 text-xs text-muted-foreground">Click a slot to set as target date</p>
                  </div>
                )}
              </div>
            )}

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="wi-category">Category</Label>
              <Select
                value={editItem?.categoryId || NONE}
                onValueChange={(v) => {
                  const next = v === NONE ? null : v;
                  const cat = categories.find(c => c.id === next);
                  setEditItem(prev => ({ ...prev, categoryId: next, category: cat?.name || '' }));
                }}
              >
                <SelectTrigger id="wi-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>None</SelectItem>
                  {categories.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="wi-project">Project</Label>
              <Select
                value={editItem?.projectId || NONE}
                onValueChange={(v) => {
                  const projectId = v === NONE ? undefined : v;
                  const project = activeProjects.find(p => p.id === projectId);
                  setEditItem(prev => {
                    const next: Partial<WorkItem> = { ...prev, projectId };
                    // Auto-set strategic driver if project has one and user hasn't set one yet.
                    if (project?.strategicDriverId && !prev?.strategicDriverId) {
                      next.strategicDriverId = project.strategicDriverId;
                    }
                    return next;
                  });
                }}
              >
                <SelectTrigger id="wi-project"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>None</SelectItem>
                  {activeProjects.map(p => <SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="wi-driver">Strategic Driver</Label>
              <Select
                value={editItem?.strategicDriverId || NONE}
                onValueChange={(v) => setEditItem(prev => ({ ...prev, strategicDriverId: v === NONE ? undefined : v }))}
              >
                <SelectTrigger id="wi-driver"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>None</SelectItem>
                  {strategicDrivers.map(d => <SelectItem key={d.id} value={d.id}>{d.title}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="wi-assign">Assign To</Label>
              <Select
                value={editItem?.assignedToExecutiveId || NONE}
                onValueChange={(v) => setEditItem(prev => ({ ...prev, assignedToExecutiveId: v === NONE ? null : v }))}
              >
                <SelectTrigger id="wi-assign"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>Unassigned</SelectItem>
                  {executives.map(exec => (
                    <SelectItem key={exec.id} value={exec.id}>{exec.displayName} ({exec.email})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <TagInput
              label="Tags"
              placeholder="Type to search or add..."
              options={allTags.map(t => t.name)}
              value={editTagNames}
              onChange={setEditTagNames}
              allowFreeText
            />

            <ChipPicker
              label="Flags"
              options={FLAG_OPTIONS}
              value={editFlags}
              onChange={setEditFlags}
            />

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="wi-notes">Notes</Label>
              <Textarea
                id="wi-notes"
                rows={2}
                value={editItem?.notes || ''}
                onChange={(e) => setEditItem(prev => ({ ...prev, notes: e.target.value }))}
              />
            </div>

            {/* Email link: mailto:, Outlook desktop deep link, or Outlook-web
                URL. Backend + helper allow-list the three shapes; inline
                validation warns before save. */}
            {(() => {
              const link = editItem?.emailLink ?? '';
              const trimmed = link.trim();
              const invalid = trimmed.length > 0 && !isAllowedEmailLink(trimmed);
              const showActions = !!trimmed && !invalid;
              return (
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="wi-email">{workItemsCopy.emailLinkLabel}</Label>
                  <div className="relative">
                    <Input
                      id="wi-email"
                      value={link}
                      onChange={(e) => setEditItem(prev => ({ ...prev, emailLink: e.target.value }))}
                      placeholder="https://outlook.office.com/mail/... or mailto:..."
                      className={cn(invalid && 'border-destructive', showActions && 'pr-20')}
                    />
                    {showActions && (
                      <div className="absolute right-1 top-1/2 flex -translate-y-1/2 gap-0.5">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button size="icon-sm" variant="ghost" onClick={() => openEmailLink(trimmed)} aria-label={workItemsCopy.openEmail}>
                              <Mail className="h-3.5 w-3.5" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>{workItemsCopy.openEmail}</TooltipContent>
                        </Tooltip>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              size="icon-sm"
                              variant="ghost"
                              onClick={() => setEditItem(prev => ({ ...prev, emailLink: '' }))}
                              aria-label={workItemsCopy.clearEmailLink}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>{workItemsCopy.clearEmailLink}</TooltipContent>
                        </Tooltip>
                      </div>
                    )}
                  </div>
                  <p className={cn('text-xs', invalid ? 'text-destructive' : 'text-muted-foreground')}>
                    {invalid ? workItemsCopy.emailLinkInvalid : workItemsCopy.emailLinkHelper}
                  </p>
                </div>
              );
            })()}

            {/* Recurrence */}
            <div className="flex items-center gap-2">
              <Checkbox
                id="wi-recurring"
                checked={editItem?.isRecurring || false}
                onCheckedChange={(checked) => setEditItem(prev => ({
                  ...prev,
                  isRecurring: !!checked,
                  ...(!checked && { recurrencePattern: undefined, recurrenceDays: undefined, recurrenceEndDate: undefined }),
                }))}
              />
              <Label htmlFor="wi-recurring" className="cursor-pointer">Recurring</Label>
            </div>
            {editItem?.isRecurring && (
              <div className="flex flex-col gap-3 pl-6">
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="wi-recur-pattern">Recurrence Pattern</Label>
                  <Select
                    value={editItem?.recurrencePattern || NONE}
                    onValueChange={(v) => setEditItem(prev => ({ ...prev, recurrencePattern: v === NONE ? undefined : v }))}
                  >
                    <SelectTrigger id="wi-recur-pattern">
                      <SelectValue placeholder="Choose pattern" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NONE}>None</SelectItem>
                      {recurrencePatterns.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                {(editItem?.recurrencePattern === 'Weekly' || editItem?.recurrencePattern === 'Biweekly') && (
                  <ChipPicker
                    label="Days"
                    options={weekDays}
                    value={editItem?.recurrenceDays ? editItem.recurrenceDays.split(',').map(d => d.trim()).filter(Boolean) : []}
                    onChange={(next) => setEditItem(prev => ({ ...prev, recurrenceDays: next.join(',') }))}
                  />
                )}
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="wi-recur-end">End Date (optional)</Label>
                  <Input
                    id="wi-recur-end"
                    type="date"
                    value={editItem?.recurrenceEndDate || ''}
                    onChange={(e) => setEditItem(prev => ({ ...prev, recurrenceEndDate: e.target.value || undefined }))}
                  />
                </div>
              </div>
            )}

            {/* Dependencies */}
            <hr className="my-1 border-border" />
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="wi-blocked-by">Blocked By</Label>
              <Select
                value={editItem?.blockedByWorkItemId || NONE}
                onValueChange={async (v) => {
                  const newValue = v === NONE ? undefined : v;
                  setEditItem(prev => ({ ...prev, blockedByWorkItemId: newValue }));
                  if (editItem?.id) {
                    try {
                      await workItemsApi.setBlockedBy(editItem.id, newValue || null);
                      loadItems();
                    } catch (err: any) {
                      console.error('Failed to set dependency', err);
                      toast({ variant: 'error', message: err?.response?.data?.error ?? 'Failed to update dependency' });
                    }
                  }
                }}
              >
                <SelectTrigger id="wi-blocked-by"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>None</SelectItem>
                  {items
                    .filter(i => i.id !== editItem?.id && !['Done', 'Cancelled'].includes(i.status))
                    .map(i => (
                      <SelectItem key={i.id} value={i.id}>{i.title}</SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>

            {/* Attachments (only for existing items) */}
            {editItem?.id && (
              <>
                <hr className="my-1 border-border" />
                <p className="flex items-center gap-1.5 text-sm font-semibold">
                  <Paperclip className="h-4 w-4" />
                  Attachments ({attachments.length}/10)
                </p>
                {attachments.length > 0 && (
                  <ul className="flex flex-col gap-1">
                    {attachments.map(att => (
                      <li
                        key={att.id}
                        className="flex items-center justify-between gap-2 rounded-lg border border-border bg-card/40 px-2 py-1.5"
                      >
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm">{att.fileName}</p>
                          <p className="text-xs text-muted-foreground">
                            {formatFileSize(att.fileSizeBytes)} · {format(new Date(att.createdAt), 'MMM d, yyyy')}
                          </p>
                        </div>
                        <div className="flex gap-0.5">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <a
                                href={workItemsApi.downloadAttachmentUrl(editItem.id!, att.id)}
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="Download"
                                className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                              >
                                <Download className="h-3.5 w-3.5" />
                              </a>
                            </TooltipTrigger>
                            <TooltipContent>Download</TooltipContent>
                          </Tooltip>
                          <Button size="icon-sm" variant="ghost" onClick={() => handleDeleteAttachment(att.id)} aria-label="Delete attachment">
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
                {attachmentUploading && (
                  <div className="my-2 flex items-center gap-2 text-xs text-muted-foreground">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" /> Uploading…
                  </div>
                )}
                <input
                  type="file"
                  ref={fileInputRef}
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleUploadAttachment(file);
                    e.target.value = '';
                  }}
                />
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={attachmentUploading || attachments.length >= 10}
                  className="mt-1 gap-1.5 self-start"
                >
                  <Paperclip className="h-3.5 w-3.5" />
                  Add Attachment (max 5 MB)
                </Button>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Detail Dialog */}
      <Dialog open={!!viewItem} onOpenChange={(o) => !o && setViewItem(null)}>
        <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
          {viewItem && (() => {
            const parsed = splitDescriptionSource(viewItem.description ?? '');
            const sourceBrand = parsed.sourceUrl ? brandForUrl(parsed.sourceUrl) : null;
            return (
            <>
              <DialogHeader>
                <div className="flex flex-wrap items-start gap-2">
                  <DialogTitle className="min-w-0 flex-1 break-words">{viewItem.title}</DialogTitle>
                  <span className="inline-flex items-center rounded-full border border-border bg-transparent px-2 py-0.5 text-xs font-medium">
                    {viewItem.type}
                  </span>
                  <PriorityChip priority={viewItem.priority} />
                  <StatusChip status={viewItem.status} />
                </div>
              </DialogHeader>
              <div className="flex flex-col gap-3 border-t border-border pt-4">
                {viewItem.status === 'Done' && (
                  <Alert variant="success" className="py-1.5">
                    <AlertDescription className="font-semibold">Completed</AlertDescription>
                  </Alert>
                )}
                {viewItem.status === 'Cancelled' && (
                  <Alert variant="warning" className="py-1.5">
                    <AlertDescription className="font-semibold">Cancelled</AlertDescription>
                  </Alert>
                )}
                {parsed.body && (
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Description</p>
                    <p className="mt-0.5 whitespace-pre-wrap text-sm">{parsed.body}</p>
                  </div>
                )}
                {parsed.sourceUrl && (
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Source</p>
                    <div className="mt-1">
                      <Button
                        size="sm"
                        variant="outline"
                        asChild
                        className="gap-1.5"
                      >
                        <a href={parsed.sourceUrl} target="_blank" rel="noopener noreferrer">
                          {sourceBrand === 'microsoft' && <MicrosoftIcon size={14} />}
                          {sourceBrand === 'google' && <GoogleIcon size={14} />}
                          {sourceBrand
                            ? `Open in ${brandLabel(sourceBrand)}`
                            : 'Open source'}
                        </a>
                      </Button>
                    </div>
                  </div>
                )}
                <div className="flex flex-wrap gap-6">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Estimated</p>
                    <p className="text-sm">{viewItem.estimatedMinutes}m</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Target Date</p>
                    <p className="text-sm">{viewItem.targetDate || 'No target date'}</p>
                  </div>
                  {viewItem.category && (
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Category</p>
                      <p className="text-sm">{viewItem.category}</p>
                    </div>
                  )}
                </div>
                {viewItem.notes && (
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Notes</p>
                    <p className="mt-0.5 whitespace-pre-wrap text-sm">{viewItem.notes}</p>
                  </div>
                )}
                {isAllowedEmailLink(viewItem.emailLink) && (
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Source email</p>
                    <div className="mt-1">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openEmailLink(viewItem.emailLink)}
                        className="gap-1.5"
                      >
                        <Mail className="h-3.5 w-3.5" />
                        {workItemsCopy.openEmail}
                      </Button>
                    </div>
                  </div>
                )}
                {viewItem.flags && viewItem.flags.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Flags</p>
                    <div className="mt-1 flex flex-wrap gap-1">
                      {viewItem.flags.map(f => (
                        <span key={f} className="inline-flex items-center rounded-full border border-border px-2 py-0.5 text-xs font-medium text-muted-foreground">
                          {f}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {viewItem.isCarryForward && (
                  <Alert variant="info" className="py-1.5">
                    <AlertDescription>
                      Carried forward{viewItem.originalDate ? ` from ${viewItem.originalDate}` : ''}
                    </AlertDescription>
                  </Alert>
                )}
                {viewItem.isRecurring && (
                  <Alert variant="info" className="py-1.5">
                    <AlertDescription>
                      Recurring: {viewItem.recurrencePattern}{viewItem.recurrenceDays ? ` (${viewItem.recurrenceDays})` : ''}
                    </AlertDescription>
                  </Alert>
                )}
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setViewItem(null)}>Close</Button>
                {viewItem.status !== 'Done' && viewItem.status !== 'Cancelled' && (
                  <Button
                    onClick={async () => { await handleComplete(viewItem.id); setViewItem(null); }}
                    className="gap-1.5 bg-emerald-600 text-white hover:bg-emerald-700"
                  >
                    <CheckCircle2 className="h-4 w-4" />
                    Mark Complete
                  </Button>
                )}
                <Button
                  variant="outline"
                  onClick={() => {
                    setEditItem(viewItem);
                    setEditFlags(viewItem.flags?.filter(f => !['CarryForward', 'ImageDerived'].includes(f)) || []);
                    loadAttachments(viewItem.id);
                    setDialogOpen(true);
                    setViewItem(null);
                  }}
                >
                  Edit
                </Button>
              </DialogFooter>
            </>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Delegate Dialog */}
      <Dialog open={delegateDialogOpen} onOpenChange={(o) => !o && setDelegateDialogOpen(false)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Delegate Work Item</DialogTitle>
          </DialogHeader>
          {delegateItem && (
            <div className="flex flex-col gap-3">
              <p className="text-sm text-muted-foreground">
                Delegating: <strong className="text-foreground">{delegateItem.title}</strong>
              </p>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="del-target">Target Executive</Label>
                <Select
                  value={delegateTarget || NONE}
                  onValueChange={(v) => setDelegateTarget(v === NONE ? '' : v)}
                  disabled={executives.length === 0}
                >
                  <SelectTrigger id="del-target">
                    <SelectValue placeholder={executives.length === 0 ? 'No executives available' : 'Choose an executive'} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>Choose an executive</SelectItem>
                    {executives.map(exec => (
                      <SelectItem key={exec.id} value={exec.id}>
                        {exec.displayName} ({exec.email})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="del-date">New Target Date (optional)</Label>
                <Input
                  id="del-date"
                  type="date"
                  value={delegateDate}
                  onChange={(e) => setDelegateDate(e.target.value)}
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="del-notes">Notes (optional)</Label>
                <Textarea
                  id="del-notes"
                  rows={2}
                  value={delegateNotes}
                  onChange={(e) => setDelegateNotes(e.target.value)}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDelegateDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleDelegate} disabled={!delegateTarget}>Delegate</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
